// Import style
import './style.scss';

// Import animations
import { fadeIn, fadeOut } from '@automator/animations';

// Import utilities
import { isDefined, isEmpty } from '@automator/utilities';

/**
 * Creates a custom context menu
 */
export default class ContextMenu {
	/**
	 * Creates and renders context menu (hidden). It also adds event listeners
	 *
	 * @param {Object} options          The options
	 * @param {String} options.id       The unique ID of the context menu
	 * @param {Node}   options.$element The element with the custom context menu
	 * @return {undefined}
	 */
	constructor( { id, $element } ) {
		// Check if we have the required data
		if ( isDefined( id ) && isDefined( $element ) ) {
			// Save data
			this.id = id;
			this.$element = $element;

			// Default groups
			this.groups = {};

			// Render the context menu
			this.render();

			// Listen right click in the element
			this.listenRightClick();

			// Handle clicks outside the context menu
			this.handleClickOutside();
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( "ContextMenu: Couldn't create menu because required data is missing." );
			}
		}
	}

	/**
	 * Creates elements to render the context menu
	 *
	 * @return {undefined}
	 */
	render() {
		// Create container
		const $contextMenu = document.createElement( 'div' );

		// Add class
		$contextMenu.classList.add( 'uap-context-menu' );

		// Save element
		this.$contextMenu = $contextMenu;

		// Add context menu to the DOM
		document.body.appendChild( $contextMenu );
	}

	/**
	 * Displays the context menu
	 *
	 * @param {Number}   x        The distance from the left border of the window
	 * @param {Number}   y        The distance from the top border of the window
	 * @param {Callback} callback The callback invoked after the context menu is displayed
	 * @return {undefined}
	 */
	show( x = 0, y = 0, callback ) {
		// Check if it's hidden
		if ( ! this.isVisible ) {
			// Set position of context menu
			this.$contextMenu.style.top = `${y}px`;
			this.$contextMenu.style.left = `${x}px`;

			// Show context menu
			fadeIn( this.$contextMenu, () => {
				// Update "isVisible"
				this.isVisible = true;

				// Trigger custom event to element
				const event = new CustomEvent( 'uap/context-menu/show' );
				this.$element.dispatchEvent( event );

				// Invoke callback
				if ( isDefined( callback ) ) {
					callback();
				}
			} );
		}
	}

	/**
	 * Hides the context menu
	 *
	 * @param {Callback} callback The callback invoked after the context menu is hidden
	 * @return {undefined}
	 */
	hide( callback ) {
		// Check if it's visible
		if ( this.isVisible ) {
			// Hide element
			fadeOut( this.$contextMenu, () => {
				// Update "isVisible"
				this.isVisible = false;

				// Trigger custom event to element
				const event = new CustomEvent( 'uap/context-menu/hide' );
				this.$element.dispatchEvent( event );

				// Invoke callback
				if ( isDefined( callback ) ) {
					callback();
				}
			} );
		}
	}

	/**
	 * Destroys the context menu. Removes a global event listener, removes the
	 * context menu from the DOM
	 *
	 * @return {undefined}
	 */
	destroy() {
		/**
		 * Destroys all groups and then removes the element in the DOM
		 * We use the function to prevent repeating code below
		 *
		 * @return {undefined}
		 */
		const helper = () => {
			// Destroy all groups
			Object.values( this.groups ).forEach( ( contextMenuGroup ) => {
				contextMenuGroup.destroy();
			} );

			// Remove main element from the DOM
			this.$contextMenu.parentNode.removeChild( this.$contextMenu );
		};

		// Hide first
		if ( this.isVisible ) {
			this.hide( () => {
				helper();
			} );
		} else {
			helper();
		}
	}

	/**
	 * Adds a group (MenuGroup)
	 *
	 * @param {Object} options      The options
	 * @param {String} options.id   The unique ID of the MenuGroup
	 * @param {String} options.name The name of the MenuGroup
	 * @return {MenuGroup}              The created MenuGroup
	 */
	addGroup( { id, name } ) {
		// Create group
		let group;

		// Check if the group already exists
		if ( isDefined( this.groups[id] ) ) {
			// Just return the existing group
			group = this.groups[id];
		} else {
			// Create a new one
			group = new MenuGroup( {
				id: id,
				name: name,
				contextMenu: this,
			} );

			// Add it to the DOM
			this.$contextMenu.appendChild( group.$menuGroup );

			// Save group
			this.groups[id] = group;
		}

		// Return the new group
		return group;
	}

	/**
	 * Removes a MenuGroup
	 *
	 * @param {String} groupID The unique ID of the MenuGroup
	 * @return {undefined}
	 */
	removeGroup( groupID ) {
		// Check if the group exists
		if ( isDefined( this.groups[groupID] ) ) {
			// Destroy the group
			this.groups[groupID].destroy();

			// Remove it from the array
			delete this.groups[groupID];
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( `ContextMenu: The group you're trying to remove (${groupID}) doesn't exist.` );
			}
		}
	}

	/**
	 * Adds event listener to listen right clicks in the targeted element
	 *
	 * @return {undefined}
	 */
	listenRightClick() {
		// Listen right click
		this.$element.addEventListener( 'contextmenu', ( event ) => {
			// Check if the user is doing a right click in the context menu itself
			if ( ! this.$contextMenu.contains( event.target ) ) {
				// Prevent default
				event.preventDefault();

				// Save current target as the "contextMenuSource"
				this.$contextMenuSource = event.target;

				// If it's not, then show the context menu
				this.show( event.pageX, event.pageY );
			}
		} );
	}

	/**
	 * Handles clicks that might be outside of the target element while this is open
	 *
	 * @return {undefined}
	 */
	handleClickOutside() {
		/**
		 * Checks if the click was outside the context menu.
		 * Callback invoked when the user clicks anywhere on the document.
		 *
		 * @param {Event} event The mouseup event
		 * @return {undefined}
		 */
		const determineClickContext = ( event ) => {
			// Check if it's visible
			if ( this.isVisible ) {
				// Check if the user clicked outside the context menu
				if ( ! this.$contextMenu.contains( event.target ) ) {
					// Check if it's clicking on the element
					if ( this.$element.contains( event.target ) && event.button == 2 ) {
						// Hide it, but show it again
						this.hide( () => {
							// Show it again
							this.show( event.pageX, event.pageY );
						} );
					} else {
						// Hide menu
						this.hide();
					}
				}
			}
		};

		// Add event listener to listen clicks outside when the context menu is displayed
		this.$element.addEventListener( 'uap/context-menu/show', () => {
			// Listen clicks outside
			document.addEventListener( 'mouseup', determineClickContext );
			document.addEventListener( 'contextmenu', determineClickContext );
		} );

		// Remove event listener to listen clicks outside when the context menu is hidden
		this.$element.addEventListener( 'uap/context-menu/hide', () => {
			// Stop listening clicks outside
			document.removeEventListener( 'mouseup', determineClickContext );
			document.removeEventListener( 'contextmenu', determineClickContext );
		} );
	}

	/**
	 * Determines the visibility of the context menu
	 *
	 * @param {Boolean} value True if it's visible
	 */
	set isVisible( value = false ) {
		this.isMenuVisible = value;
	}

	/**
	 * Returns the visibility status of the context menu
	 *
	 * @return {Boolean} True if the context menu is visible
	 */
	get isVisible() {
		let isMenuVisible = false;

		// Check if the real property exists
		if ( isDefined( this.isMenuVisible ) ) {
			isMenuVisible = this.isMenuVisible;
		}

		return isMenuVisible;
	}
}

/**
 * Creates a context menu group
 */
class MenuGroup {
	/**
	 * Creates and renders context menu group.
	 *
	 * @param {Object}      options             The options
	 * @param {String}      options.id          The unique ID of the context menu
	 * @param {String}      options.name        The name of the group (optional)
	 * @param {ContextMenu} options.contextMenu The parent ContextMenu
	 * @return {undefined}
	 */
	constructor( { id, name = '', contextMenu } ) {
		// Check if we have the required data
		if ( ! isEmpty( id ) ) {
			// Save data
			this.id = id;
			this.name = name;
			this.contextMenu = contextMenu;

			// Default items
			this.items = {};

			// Render
			this.render();
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( "ContextMenu/MenuGroup: Couldn't create group because required data is missing." );
			}
		}
	}

	/**
	 * Creates elements to render the context menu group
	 *
	 * @return {undefined}
	 */
	render() {
		// Create container
		const $container = document.createElement( 'div' );

		// Add main class
		$container.classList.add( 'uap-context-menu-group' );

		// Check if we have to add a title
		if ( ! isEmpty( this.name ) ) {
			// Add title
			$container.insertAdjacentHTML(
				'beforeend',
				`
				<div class="uap-context-menu-group-title">${this.name}</div>
			`
			);
		}

		this.$menuGroup = $container;
	}

	/**
	 * Adds an item (MenuItem)
	 *
	 * @param {Object}   options          The options
	 * @param {String}   options.id       The unique id of the MenuItem
	 * @param {String}   options.icon     The id of the icon
	 * @param {String}   options.name     The name of the item
	 * @param {Boolean}  options.disabled True if the item is disabled
	 * @param {Callback} options.callback Function invoked when the user clicks the item
	 * @return {MenuItem}                  The created MenuItem
	 */
	addItem( { id, icon, name, disabled, callback } ) {
		// Create item
		let item;

		// Check if the item already exists
		if ( isDefined( this.items[id] ) ) {
			// Just return the existing item
			item = this.items[id];
		} else {
			// Create a new one
			item = new MenuItem( {
				id: id,
				icon: icon,
				name: name,
				disabled: disabled,
				callback: callback,
				contextMenu: this.contextMenu,
				contextMenuGroup: this,
			} );

			// Add it to the DOM
			this.$menuGroup.appendChild( item.$menuItem );

			// Save item
			this.items[id] = item;
		}

		// Return the new group
		return item;
	}

	/**
	 * Removes a MenuItem
	 *
	 * @param {String} itemID The unique ID of the MenuItem
	 * @return {undefined}
	 */
	removeItem( itemID ) {
		// Check if the item exists
		if ( isDefined( this.items[itemID] ) ) {
			// Destroy the item
			this.items[itemID].destroy();

			// Remove it from the array
			delete this.items[itemID];
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( `ContextMenu/MenuGroup: The item you're trying to remove (${itemID}) doesn't exist.` );
			}
		}
	}

	/**
	 * Destroys the context menu group. Removes the element from the DOM
	 *
	 * @return {undefined}
	 */
	destroy() {
		// Remove the main element
		this.$menuGroup.parentNode.removeChild( this.$menuGroup );
	}
}

/**
 * Creates a context menu item
 */
class MenuItem {
	/**
	 * Creates the item
	 *
	 * @param {Object}      options                  The options
	 * @param {String}      options.id               The unique id of the MenuItem
	 * @param {String}      options.icon             The id of the icon
	 * @param {String}      options.name             The name of the item
	 * @param {Boolean}     options.disabled         True if the item is disabled
	 * @param {Callback}    options.callback         Function invoked when the user clicks the item
	 * @param {ContextMenu} options.contextMenu      The parent ContextMenu
	 * @param {MenuGroup}   options.contextMenuGroup The parent MenuGroup
	 * @return {undefined}
	 */
	constructor( { id, icon, name, disabled = false, callback, contextMenu, contextMenuGroup } ) {
		// Check if we have the required data
		if ( ! isEmpty( id ) && ! isEmpty( name ) ) {
			// Save data
			this.id = id;
			this.icon = icon;
			this.name = name;
			this.disabled = disabled;
			this.callback = callback;
			this.contextMenu = contextMenu;
			this.contextMenuGroup = contextMenuGroup;

			// Render
			this.render();

			// Check if the item is not disabled
			if ( ! this.disabled ) {
				// Listen clicks
				this.listenClick();
			}
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( "ContextMenu/MenuItem: Couldn't create item because required data is missing." );
			}
		}
	}

	/**
	 * Creates elements to render the context menu item
	 *
	 * @return {undefined}
	 */
	render() {
		// Create container
		const $container = document.createElement( 'div' );

		// Add main class
		$container.classList.add( 'uap-context-menu-group-item' );

		// Check if the item is disabled
		if ( this.disabled ) {
			// Add disabled class
			$container.classList.add( 'uap-context-menu-group-item--disabled' );
		}

		// Add text
		$container.innerHTML = this.name;

		// Save item
		this.$menuItem = $container;
	}

	/**
	 * Listen clicks to the item and invoke the callback
	 *
	 * @return {undefined}
	 */
	listenClick() {
		// Listen clicks to the item
		this.$menuItem.addEventListener( 'click', () => {
			// Invoke the callback
			if ( isDefined( this.callback ) ) {
				this.callback();
			}

			// Hide context menu
			this.contextMenu.hide();
		} );
	}

	/**
	 * Destroys the context menu item. Removes the element from the DOM
	 *
	 * @return {undefined}
	 */
	destroy() {
		// Remove the main element
		this.$menuItem.parentNode.removeChild( this.$menuItem );
	}
}

// Temporary solution until we merge /recipe-ui/ with /backend/
window.uap = {};
window.uap.ContextMenu = ContextMenu;
